import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import CoworkingTaskAvatar from "../components/CoworkingTaskAvatar";
import { API } from "../lib/api";
import { formatCoworkingMmss, progressFromTimer, remainingFromTimer } from "../lib/coworkingTimer";

const POLL_MS = 1000;
const TICK_MS = 250;
const CONFETTI_MS = 2400;
const CONFETTI_COUNT = 56;

function useTransparentOverlayRoot() {
  useEffect(() => {
    const previous = {
      html: document.documentElement.style.background,
      body: document.body.style.background,
      root: document.getElementById("root")?.style.background || "",
    };
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    if (document.getElementById("root")) document.getElementById("root").style.background = "transparent";
    return () => {
      document.documentElement.style.background = previous.html;
      document.body.style.background = previous.body;
      if (document.getElementById("root")) document.getElementById("root").style.background = previous.root;
    };
  }, []);
}

function useCoworkingState() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch(`${API}/overlay/coworking`, {
          cache: "no-store",
          credentials: "omit",
        });
        if (!response.ok) return;
        const data = await response.json();
        if (active) setState(data);
      } catch (_) {
        // Keep last good frame in OBS when the API blips.
      }
    };
    refresh();
    const timer = window.setInterval(refresh, POLL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  return state;
}

function modeFromPath(pathname) {
  if (pathname.endsWith("/timer")) return "timer";
  if (pathname.endsWith("/tasks")) return "tasks";
  return "all";
}

function taskLabel(task) {
  return task?.preferred_name || task?.display_name || task?.login || "viewer";
}

/** Celebration blip via Web Audio. Fails soft if autoplay is blocked. */
function playConfettiBlip() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = playConfettiBlip._ctx || new AudioCtx();
    playConfettiBlip._ctx = ctx;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    const master = ctx.createGain();
    // Louder peak so OBS "Control audio via OBS" can pick it up on stream.
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.42, now + 0.014);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);
    master.connect(ctx.destination);

    const freqs = [660, 990, 1320, 1760];
    freqs.forEach((freq, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = index % 2 === 0 ? "triangle" : "sine";
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.12, now + 0.12);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.22 / (index + 1), now + 0.01 + index * 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22 + index * 0.04);
      osc.connect(gain);
      gain.connect(master);
      osc.start(now + index * 0.016);
      osc.stop(now + 0.32 + index * 0.04);
    });
  } catch (_) {
    // Browser/OBS may block audio until Control audio via OBS is enabled.
  }
}

/** Warm the audio graph so the first celebration is less likely to be silent in OBS. */
function warmConfettiAudio() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = playConfettiBlip._ctx || new AudioCtx();
    playConfettiBlip._ctx = ctx;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.00001, now);
    gain.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.frequency.setValueAtTime(40, now);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.03);
  } catch (_) {
    // Ignore warm-up failures.
  }
}

function useSmoothTimer(timer) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (String(timer?.status || "") !== "running") return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [timer?.status, timer?.ends_at, timer?.remaining_seconds, timer?.server_now]);

  return useMemo(() => {
    const remaining = remainingFromTimer(timer, nowMs);
    return {
      ...timer,
      remaining_seconds: remaining,
      display: formatCoworkingMmss(remaining),
      progress: progressFromTimer(timer, remaining),
    };
  }, [timer, nowMs]);
}

function TimerPanel({ timer, enabled }) {
  if (!timer) return null;
  const phaseLabel = timer.phase === "break" ? "Break" : "Focus";
  const statusLabel = timer.status === "running" ? "Live" : timer.status === "paused" ? "Paused" : "Ready";
  return (
    <section className="coworking-panel coworking-timer-panel" data-testid="coworking-timer">
      <div className="coworking-eyebrow">
        <span>{phaseLabel}</span>
        <span className={`coworking-status coworking-status-${timer.status}`}>{statusLabel}</span>
      </div>
      <div className="coworking-clock">{timer.display || "00:00"}</div>
      <div className="coworking-progress" aria-hidden="true">
        <div className="coworking-progress-fill" style={{ width: `${Math.round((timer.progress || 0) * 100)}%` }} />
      </div>
      {!enabled && <div className="coworking-muted">Suite off · enable in Admin → Coworking</div>}
    </section>
  );
}

function StatsStrip({ stats }) {
  const people = Number(stats?.people_coworking || 0);
  const completed = Number(stats?.tasks_completed || 0);
  const open = Number(stats?.open_tasks || 0);
  return (
    <section className="coworking-panel coworking-stats-panel" data-testid="coworking-stats">
      <div className="coworking-stats-grid">
        <div className="coworking-stat">
          <div className="coworking-stat-value">{people}</div>
          <div className="coworking-stat-label">Coworking</div>
        </div>
        <div className="coworking-stat">
          <div className="coworking-stat-value">{completed}</div>
          <div className="coworking-stat-label">Done</div>
        </div>
        <div className="coworking-stat">
          <div className="coworking-stat-value">{open}</div>
          <div className="coworking-stat-label">Open</div>
        </div>
      </div>
    </section>
  );
}

function TasksPanel({ tasks }) {
  const rows = Array.isArray(tasks) ? tasks.slice(0, 8) : [];
  return (
    <section className="coworking-panel coworking-tasks-panel" data-testid="coworking-tasks">
      <div className="coworking-eyebrow">
        <span>Open tasks</span>
        <span className="coworking-task-count">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="coworking-muted">!task to join the board</div>
      ) : (
        <ol className="coworking-task-list">
          {rows.map((task) => (
            <li key={task.id || `${task.login}-${task.text}`}>
              <CoworkingTaskAvatar
                avatarUrl={task.avatar_url}
                name={taskLabel(task)}
                size={32}
              />
              <div className="coworking-task-copy">
                <span className="coworking-task-name">{taskLabel(task)}</span>
                <span className="coworking-task-text">{task.text}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ConfettiBurst() {
  const pieces = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, index) => ({
        id: index,
        left: 2 + ((index * 17) % 96),
        delay: (index % 10) * 55,
        duration: 1400 + (index % 7) * 160,
        drift: ((index % 11) - 5) * 28,
        rotate: 180 + (index % 8) * 55,
        color: ["#007aff", "#30d158", "#ff9f0a", "#ff3b30", "#bf5af2", "#e5e5ea", "#64d2ff"][index % 7],
        size: 8 + (index % 5) * 2,
        top: 4 + (index % 9) * 3,
      })),
    [],
  );

  return (
    <div className="coworking-confetti" aria-hidden="true" data-testid="coworking-confetti">
      <div className="coworking-confetti-flash" />
      {pieces.map((piece) => (
        <span
          key={piece.id}
          className="coworking-confetti-piece"
          style={{
            left: `${piece.left}%`,
            top: `${piece.top}%`,
            width: piece.size,
            height: piece.size * 1.55,
            background: piece.color,
            animationDelay: `${piece.delay}ms`,
            animationDuration: `${piece.duration}ms`,
            "--coworking-confetti-x": `${piece.drift}px`,
            "--coworking-confetti-rot": `${piece.rotate}deg`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Fire a full-overlay celebration when open tasks drop or session done count rises.
 * Works on /timer, /tasks, and combined overlays (including offline stream / admin-driven completes).
 */
function useTaskCompletionConfetti(tasks, completedCount, soundEnabled) {
  const [burstKey, setBurstKey] = useState(0);
  const previousIdsRef = useRef(null);
  const previousCompletedRef = useRef(null);
  const clearTimerRef = useRef(0);

  useEffect(() => {
    warmConfettiAudio();
  }, []);

  useEffect(() => {
    const ids = new Set((Array.isArray(tasks) ? tasks : []).map((task) => String(task.id || "")).filter(Boolean));
    const previous = previousIdsRef.current;
    const prevCompleted = previousCompletedRef.current;
    const nextCompleted = Number(completedCount || 0);
    previousIdsRef.current = ids;
    previousCompletedRef.current = nextCompleted;

    if (previous === null || prevCompleted === null) return undefined;

    let completed = 0;
    previous.forEach((id) => {
      if (!ids.has(id)) completed += 1;
    });
    const completedDelta = nextCompleted > prevCompleted ? nextCompleted - prevCompleted : 0;
    if (completed <= 0 && completedDelta <= 0) return undefined;

    setBurstKey((value) => value + 1);
    if (soundEnabled) playConfettiBlip();
    window.clearTimeout(clearTimerRef.current);
    clearTimerRef.current = window.setTimeout(() => setBurstKey(0), CONFETTI_MS);
    return undefined;
  }, [tasks, completedCount, soundEnabled]);

  useEffect(() => () => window.clearTimeout(clearTimerRef.current), []);

  return burstKey;
}

export default function CoworkingOverlay() {
  useTransparentOverlayRoot();
  const location = useLocation();
  const mode = useMemo(() => modeFromPath(location.pathname), [location.pathname]);
  const state = useCoworkingState();
  const enabled = Boolean(state?.enabled);
  const soundEnabled = state?.confetti_sound !== false;
  const tasks = Array.isArray(state?.tasks) ? state.tasks : [];
  const stats = state?.stats || {
    people_coworking: 0,
    tasks_completed: 0,
    open_tasks: tasks.length,
  };
  const showTimer = mode === "all" || mode === "timer";
  const showTasks = mode === "all" || mode === "tasks";
  const smoothTimer = useSmoothTimer(state?.timer);
  // Always track completion signals so /timer and /tasks both celebrate on stream.
  const confettiBurst = useTaskCompletionConfetti(tasks, stats.tasks_completed, soundEnabled);

  // Parker MAIN placement: CSS --coworking-safe-top clears the black header on all modes.
  // Left column = timer + stats. Right column = tasks (inset from TR shoutouts).
  return (
    <main
      className={`coworking-overlay coworking-overlay-${mode}`}
      data-testid="coworking-overlay"
      data-mode={mode}
      aria-live="polite"
    >
      {confettiBurst ? <ConfettiBurst key={confettiBurst} /> : null}
      <div className="coworking-layout">
        {showTimer && (
          <div className="coworking-col coworking-col-left" data-testid="coworking-left-column">
            <TimerPanel timer={smoothTimer} enabled={enabled} />
            <StatsStrip stats={stats} />
          </div>
        )}
        {showTasks && (
          <div className="coworking-col coworking-col-right" data-testid="coworking-right-column">
            <TasksPanel tasks={tasks} />
          </div>
        )}
      </div>
    </main>
  );
}
